// English strings for GetPaid's invoices feature: list, create form, and
// detail page (payment link, invite buyer, cancel). GetPaid always invoices
// as the org's own (self) account, so there is no seller-picker copy here.
const invoices: Record<string, string> = {
  'invoices.title': 'Invoices',
  'invoices.new': 'New invoice',
  'invoices.loading': 'Loading invoices...',
  'invoices.error': 'Failed to load invoices.',
  'invoices.empty.title': 'No invoices yet',
  'invoices.empty.description': 'Send your first invoice and start getting paid.',
  'invoices.empty.cta': 'Create your first invoice',
  'invoices.column.status': 'Status',
  'invoices.column.amount': 'Amount',
  'invoices.column.buyer': 'Buyer',
  'invoices.column.createdAt': 'Created',

  // Create form
  'invoices.create.title': 'New invoice',
  'invoices.create.subtitle': 'Send an invoice to your buyer and start tracking payment.',
  'invoices.create.section.buyer': 'Buyer',
  'invoices.create.section.buyer.description': 'Who this invoice is sent to.',
  'invoices.create.section.lineItems': 'Line items',
  'invoices.create.section.lineItems.description':
    'Subtotal is computed as quantity multiplied by unit price.',
  'invoices.create.section.details': 'Invoice details',
  'invoices.create.section.details.description': 'Currency and an optional due date.',
  'invoices.create.field.buyerEmail': 'Buyer email',
  'invoices.create.field.buyerName': 'Buyer name',
  'invoices.create.field.buyerCountry': 'Buyer country (optional)',
  'invoices.create.field.buyerRef': 'Buyer reference',
  'invoices.create.field.currency': 'Currency',
  'invoices.create.field.dueDate': 'Due date (optional)',
  'invoices.create.field.incoterm': 'Incoterm',
  'invoices.create.field.incoterm.none': 'None',
  'invoices.create.field.portOfLoading': 'Port of loading',
  'invoices.create.field.exportContractRef': 'Export contract ref',
  'invoices.create.field.description': 'Description',
  'invoices.create.field.description.placeholder': 'Item or service',
  'invoices.create.field.descriptionAria': 'Description for line {index}',
  'invoices.create.field.quantity': 'Quantity',
  'invoices.create.field.quantityAria': 'Quantity for line {index}',
  'invoices.create.field.unitPrice': 'Unit price',
  'invoices.create.field.unitPriceAria': 'Unit price for line {index}',
  'invoices.create.field.subtotal': 'Subtotal',
  'invoices.create.addLineItem': 'Add line item',
  'invoices.create.removeLineItemAria': 'Remove line {index}',
  'invoices.create.total': 'Total',
  'invoices.create.exportDetails.toggle': 'Export details (optional)',
  'invoices.create.minAmount.note':
    'Buyers pay by bank transfer through our payments partner, which requires a minimum of about {min} (or equivalent). Smaller invoices can’t be funded online.',
  'invoices.create.minAmount.below':
    'Below the {min} minimum your buyer can pay by bank transfer — increase the total or this invoice can’t be funded online.',
  'invoices.create.submit': 'Create invoice',
  'invoices.create.submitting': 'Creating...',
  'invoices.create.cancel': 'Cancel',
  'invoices.create.error.generic': 'Failed to create invoice.',
  'invoices.create.error.validation': 'Please fix the highlighted fields.',
  'invoices.create.error.conflict': 'An invoice with this reference already exists.',
  'invoices.create.error.conflict.link': 'Open existing invoice',
  'invoices.create.error.selfSellerNotReady': 'Finish onboarding to start invoicing.',
  'invoices.create.error.selfSellerNotReady.description':
    'We need to verify your business and set up your Pix payout before you can send invoices.',
  'invoices.create.error.selfSellerNotReady.cta': 'Finish onboarding',

  // Detail page
  'invoices.detail.title': 'Invoice',
  'invoices.detail.loading': 'Loading invoice...',
  'invoices.detail.error': 'Failed to load invoice.',
  'invoices.detail.field.buyer': 'Buyer',
  'invoices.detail.field.buyerEmail': 'Buyer email',
  'invoices.detail.field.total': 'Total',
  'invoices.detail.field.createdAt': 'Created',
  'invoices.detail.field.dueDate': 'Due date',
  'invoices.detail.field.cancelReason': 'Cancel reason',
  'invoices.detail.section.lineItems': 'Line items',
  'invoices.detail.section.timeline': 'Payment timeline',
  'invoices.detail.column.description': 'Description',
  'invoices.detail.column.quantity': 'Qty',
  'invoices.detail.column.unitPrice': 'Unit price',
  'invoices.detail.column.subtotal': 'Subtotal',
  'invoices.detail.lineItems.empty': 'No line items.',
  'invoices.detail.timeline.empty': 'No events yet.',
  'invoices.detail.paymentLink.title': 'Payment link',
  'invoices.detail.paymentLink.copyButton': 'Copy payment link',
  'invoices.detail.paymentLink.copyError': 'Could not copy link',
  'invoices.detail.paymentLink.disabledHint':
    'The payment link is no longer relevant for this invoice.',
  'invoices.detail.actions.title': 'Actions',
  'invoices.detail.actions.cancel': 'Cancel invoice',
  'invoices.detail.actions.cancel.disabledHint':
    'Invoice cannot be cancelled in its current state.',
  'invoices.detail.action.invite.button': 'Email the payment link',
  'invoices.detail.action.invite.button.resend': 'Resend invite email',
  'invoices.detail.action.invite.disabledHint':
    'The buyer can no longer be invited for this invoice.',
  'invoices.detail.action.invite.error': 'Failed to invite buyer.',
  'invoices.detail.action.invite.dialog.title': 'Email the buyer?',
  'invoices.detail.action.invite.dialog.title.resend': 'Resend the invite email?',
  'invoices.detail.action.invite.dialog.description':
    'We will email the payment link to the buyer below so they can pay this invoice.',
  'invoices.detail.action.invite.dialog.description.resend':
    'We will email another payment link to the buyer below in case the previous email was missed.',
  'invoices.detail.action.invite.dialog.emailLabel': 'Buyer email',
  'invoices.detail.action.invite.dialog.confirm': 'Send email',
  'invoices.detail.action.invite.dialog.confirm.resend': 'Resend email',
  'invoices.detail.action.invite.dialog.submitting': 'Sending...',
  'invoices.detail.action.invite.dialog.dismiss': 'Not yet',
  'invoices.detail.cancel.title': 'Cancel invoice?',
  'invoices.detail.cancel.description': 'This stops the invoice from progressing. Let us know why.',
  'invoices.detail.cancel.reason': 'Reason',
  'invoices.detail.cancel.reason.placeholder': 'e.g. Buyer requested termination',
  'invoices.detail.cancel.confirm': 'Cancel invoice',
  'invoices.detail.cancel.submitting': 'Cancelling...',
  'invoices.detail.cancel.dismiss': 'Keep invoice',
  'invoices.detail.cancel.error': 'Failed to cancel invoice.',
};

export default invoices;
